const canonicalUrl = 'https://open-racer.com'

const route = (path, title, summary) => ({
  path,
  url: `${canonicalUrl}${path}`,
  title,
  summary,
})

const buildProfile = () => ({
  title: 'Open Racer',
  canonicalUrl,
  summary: 'Open Racer helps drivers find tracks, plan racing lines, and turn supported motorsport telemetry into measured reports.',
  routes: {
    home: route('/', 'Open Racer', 'Product overview and recent Open Racer content.'),
    tracks: route('/go-karting-tracks', 'Track discovery', 'Find racetracks and inspect public community racing lines.'),
    racingLineEditor: route('/racing-line-editor', 'Racing Line Editor', 'Learn the racing-line workflow and browse recent public lines.'),
    webMcpEditor: {
      path: '/',
      url: 'https://editor.open-racer.com',
      title: 'WebMCP racing line editor',
      summary: 'Standalone browser-local racing line editor with WebMCP editing tools and JSON import and export.',
    },
    iracing: route('/iracing', 'iRacing telemetry analysis', 'Create a measured report from supported iRacing telemetry.'),
    alfano: route('/alfano-racing-line-importer', 'Alfano data analysis', 'Create a measured report from a supported Alfano export.'),
    gopro: route('/gopro-racing-line-importer', 'GoPro racing line importer', 'Use compatible GoPro GPS data as a racing-line source.'),
    iracingSample: route('/iracing-coaching-report', 'iRacing sample report', 'Public example of an Open Racer iRacing report.'),
    alfanoSample: route('/alfano-coaching-report', 'Alfano sample report', 'Public example of an Open Racer Alfano report.'),
    pricing: route('/pricing', 'Pricing', 'Current Open Racer access and Paddock Pass information.'),
    llms: route('/llms.txt', 'LLM index', 'Compact, canonical Open Racer index for AI agents.'),
    aiGuide: route('/ai-guide.md', 'AI agent guide', 'Detailed product routes, evidence rules, and agent boundaries.'),
  },
  capabilities: [
    'Find racetracks and public community racing lines.',
    'Plan racing lines on a track map.',
    'Create measured reports from supported iRacing and Alfano data.',
    'Use compatible GoPro GPS data as a racing-line source.',
    'Inspect public iRacing and Alfano sample reports before using an upload workflow.',
  ],
  boundaries: [
    'Treat measurements as evidence, not proof of causality or guaranteed lap-time improvement.',
    'The user must choose any local file and intentionally start any upload or import.',
    'Do not create, import, duplicate, save, publish, subscribe, or purchase without the user taking the corresponding visible action.',
    'Viewing public material is separate from creating or importing. Current eligibility and pricing are authoritative on the visible product and pricing pages.',
  ],
})

const getAgentProfile = () => buildProfile()

const renderLlmsTxt = () => {
  const profile = buildProfile()
  const r = profile.routes
  return `# Open Racer

> ${profile.summary}

Open Racer is a web product for track discovery, racing-line preparation, and evidence-based telemetry review. Prefer the canonical routes below instead of guessing URLs.

## Main products

- [Track discovery](${r.tracks.url}): ${r.tracks.summary}
- [Racing Line Editor](${r.racingLineEditor.url}): ${r.racingLineEditor.summary}
- [WebMCP racing line editor](${r.webMcpEditor.url}): ${r.webMcpEditor.summary}
- [iRacing telemetry analysis](${r.iracing.url}): ${r.iracing.summary}
- [Alfano data analysis](${r.alfano.url}): ${r.alfano.summary}
- [GoPro racing line importer](${r.gopro.url}): ${r.gopro.summary}
- [Pricing](${r.pricing.url}): ${r.pricing.summary}

## Public examples

- [iRacing sample report](${r.iracingSample.url})
- [Alfano sample report](${r.alfanoSample.url})

## Agent resources

- [Detailed AI agent guide](${r.aiGuide.url})
- [XML sitemap](${canonicalUrl}/sitemap.xml)

## Boundaries

- Treat measured telemetry as evidence, not proof of causality or guaranteed performance improvement.
- The user must choose any local file and intentionally start uploads or imports.
- Do not upload, import, create, save, publish, subscribe, or purchase on the user's behalf.
- WebMCP tools on the main site provide read-only discovery and guidance. The standalone editor exposes browser-local editing tools.
`
}

const renderAiGuide = () => {
  const profile = buildProfile()
  const r = profile.routes
  return `# Open Racer agent guide

Canonical site: ${profile.canonicalUrl}

${profile.summary}

## Supported capabilities

- Find tracks and inspect public community racing lines.
- Prepare and review racing lines on track maps.
- Build evidence-based reports from supported iRacing telemetry and Alfano exports.
- Use compatible GoPro GPS data as a racing-line source.
- Review public samples as examples before asking a user to provide data.

## Canonical routes

- Track discovery: ${r.tracks.url}
- Racing Line Editor overview: ${r.racingLineEditor.url}
- Standalone WebMCP editor: ${r.webMcpEditor.url}
- iRacing: ${r.iracing.url}
- Alfano: ${r.alfano.url}
- GoPro: ${r.gopro.url}
- iRacing sample: ${r.iracingSample.url}
- Alfano sample: ${r.alfanoSample.url}
- Pricing: ${r.pricing.url}

## Recommended workflows

### Track and racing-line preparation

1. Use track discovery to find the intended circuit.
2. Inspect public lines only as references, not as guaranteed fastest lines.
3. Let the user select or create the line they intend to work with.
4. Keep braking, acceleration, and coasting advice tied to visible track context and available evidence.
5. Leave saving, publication, duplication, and account decisions to the user.

### Measured telemetry

1. Confirm whether the source is iRacing, Alfano, or compatible GoPro GPS data.
2. Use the matching canonical product route.
3. The user must choose any local file and intentionally start the upload or import.
4. Wait for the visible product to validate and process the source.
5. Distinguish measured observations from authored coaching. Correlation in telemetry does not prove causality.
6. Use the public samples as examples of report structure, not evidence about another driver's session.

## WebMCP

Compatible browsers expose Open Racer's main-site tools through \`document.modelContext\`. These tools provide read-only discovery and guidance, return canonical routes, and describe the current product context. They do not navigate, upload, create, edit, save, publish, subscribe, or purchase.

The standalone browser-local editor exposes editing tools at ${r.webMcpEditor.url}. Its lines are stored in the browser and can be imported or exported as JSON. Confirm the visible editor state before changing it and preserve the user's ability to undo or export.

WebMCP is progressively enhanced. If \`document.modelContext\` is unavailable, use this guide and the visible website instead.

## Safety and access boundaries

- Never infer permission to use local files, account data, or paid features.
- Do not bypass authentication, ownership, product limits, or visible confirmations.
- Do not claim a racing line or coaching suggestion is definitively fastest without sufficient evidence.
- Do not describe sample data as belonging to the current user.
- Viewing public pages is separate from creating, importing, or duplicating. Refer to the current pricing and product UI for authoritative access rules.
- Preserve source attribution and distinguish public facts, measured data, and generated suggestions.
`
}

module.exports = {
  getAgentProfile,
  renderAiGuide,
  renderLlmsTxt,
}
